import { useNavigation } from "@react-navigation/native"
import { Button, ErrorMessage, Screen, Text, TextInput } from "components"
import { translate } from "i18n"
import React, { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { Image, View, TouchableOpacity } from "react-native"
import { style } from "./styles"
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from "react-native-confirmation-code-field"
import flash from "config/flash"
import { colors } from "colors"
import { typography } from "theme"
import { get } from "lodash"
import { useStores } from "models"
import { useSessionExpired } from "utils/function"
import { useColor } from "app/theme/getThemes"
import { CheckBox } from "components/CheckBox"
import { PhoneNumberInput } from "components/PhoneNumberInput"
import { ICountry } from "react-native-international-phone-number"
import { observer } from "mobx-react-lite"

export const LoginScreen = observer(() => {
  const { companyTheme } = useColor()
  const { userStore } = useStores()
  const { flagInfo, setFlagInfo } = userStore
  const navigation = useNavigation<any>()
  const [isLoading, setIsLoading] = useState(false)
  const { control, handleSubmit, setValue, watch, clearErrors } = useForm({
    reValidateMode: "onSubmit",
  })

  // WH
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [loginCountry, setLoginCountry] = useState({})
  const [defaultCountry, setDefaultCountry] = useState<any>("MY")

  const ref = useBlurOnFulfill({ value: watch("pin"), cellCount: 6 })
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value: watch("pin"),
    setValue: (e) => setValue("pin", e),
  })

  useEffect(() => {
    if (userStore?.savedMe) {
      setValue("phoneNumber", userStore?.savedPhoneNum)
      setValue("pin", userStore?.savedPinNum)
      setIsChecked(userStore?.savedMe)
      setLoginCountry({ callingCode: "+60", cca2: "MY", flag: "MY" })
      setDefaultCountry(flagInfo.cca2)
    }
  }, [flagInfo])

  const flagOnChange = (data: any) => {
    setFlagInfo(data)
    console.log("THIS: ", flagInfo)
  }

  const onSubmit = async (data) => {
    console.log("flagInfo: ", flagInfo)
    const phoneNum = flagInfo.callingCode + data?.phoneNumber
    const phoneNumLast = phoneNum.replace(/\s/g, "")
    console.log("phoneNumLast: ", phoneNumLast)
    const formattedPhone = phoneNumLast.startsWith("+") ? phoneNumLast?.slice(1) : phoneNumLast

    setIsLoading(true)
    await fetch(`${process.env.API_URL}/api/client/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contact_number: formattedPhone,
        pwd: data?.pin,
        company_code: process.env.COMPANY_CODE,
      }),
    })
      .then(async (res) => {
        setIsLoading(false)
        if (res?.ok) {
          const responseData = await res?.json()

          await fetch(`${process.env.API_URL}/api/client/profile`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `bearer ${responseData?.accessToken}`,
            },
          })
            .then(async (res) => {
              console.log(res)
              if (res?.ok) {
                const datas = await res?.json()
                userStore.update({
                  id: get(responseData, "member_id", "").toString(),
                  accessToken: get(responseData, "accessToken", ""),
                  fullName: get(responseData, "full_name"),
                  memberCode: datas?.data?.contact_number,
                  profileImage: datas?.data.profile_picture,
                  real_member_code: datas?.data.member_code,
                  referral_code: datas?.data.referral_code,
                  savedMe: isChecked,
                  savedPhoneNum: isChecked ? data?.phoneNumber : null,
                  savedPinNum: isChecked ? data?.pin : null,
                  flagInfo: flagInfo,
                })

                userStore?.updateIsLoggedIn(true)
                flash("success", translate("loginScreen.loginSuccess"))

                navigation.reset({ index: 0, routes: [{ name: "app" }] })
              } else {
                const errorMsg = await res?.json()
                if (errorMsg?.error === "Token expired" || errorMsg?.error === "Invalid token") {
                  useSessionExpired()
                } else {
                  flash("error", translate("errors.failedToRetreiveMember"))
                }
              }
            })
            .catch((e) => {
              flash("error", translate("errors.failedToRetreiveMember"))
              console.error(e)
            })
        } else {
          const errorMsg = await res?.json()

          if (errorMsg?.error === "INVALID_CREDENTIAL") {
            // User found but inccorect password
            flash("error", translate("loginScreen.invalidCredential"))
          } else if (errorMsg?.error === "NEED_OTP_VERIFICATION") {
            // Need to verify user
            flash("error", translate("loginScreen.verifyAccount"))
            navigation.navigate("verification", {
              routeFrom: "login",
              phoneNumber: formattedPhone,
            })

            // send otp
            await fetch(`${process.env.API_URL}/api/client/otp/${formattedPhone}`, {
              method: "POST",
              body: JSON.stringify({
                configure_name: "whatsapp",
                company_code: process.env.COMPANY_CODE,
              }),
              headers: {
                "Content-Type": "application/json",
              },
            })
          } else if (errorMsg?.error === "STATUS_INACTIVE") {
            flash("error", translate("loginScreen.contactUs"))
          } else if (errorMsg?.error === "STATUS_EXISTING") {
            flash("error", "Existing members detected. Please proceed with this account.")
            navigation.navigate("existing", {
              phoneNumber: data?.phoneNumber,
              countryCode: flagInfo.callingCode,
              countryCca2: flagInfo.cca2,
            })
          } else {
            flash("error", translate("loginScreen.failedToLogin"))
          }
        }
      })
      .catch((err) => {
        flash("error", translate("loginScreen.failedToLogin"))
        console.log(err)
        setIsLoading(false)
      })
  }

  //// Login Page Logo
  // Dekode - "assets/dekode-icon.png"
  // Larrie - "assets/larrie-logo.png"
  // AKEMI - "assets/akemi-logo.png"

  return (
    <Screen preset="scroll" contentContainerStyle={{ height: "100%" }}>
      <View style={style.container}>
        <Image source={require("assets/wellnight-logo.png")} style={style.logoImage} />
        <View style={style.mv30}>
          <Text style={style.titleText}>{translate("loginScreen.header")} LARRIE</Text>
          <Text style={{ color: colors.lightGrey, fontFamily: typography.primary.normal }}>
            {translate("loginScreen.description")}
          </Text>
        </View>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: translate("errors.required"),
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <PhoneNumberInput
                value={value || ""}
                onChange={(formattedPhoneNumber) => {
                  onChange(formattedPhoneNumber)
                }}
                onCustomFocus={() => clearErrors("phoneNumber")}
                fieldError={error}
                errorMessage={error?.message}
                placeholder={translate("loginScreen.phoneNumberPlaceholder")}
                title={
                  <Text>
                    {translate("loginScreen.phoneNumber")}
                    <Text style={{ color: "red" }}>*</Text>
                  </Text>
                }
                containerStyle={style.inputContainer}
                inputStyle={style.inputStyle}
                onCountryChange={(country) => flagOnChange(country)}
                defaultCountry={flagInfo.cca2}
              />
            )
          }}
        />

        <Controller
          name="pin"
          control={control}
          rules={{
            required: translate("errors.required"),
            minLength: { value: 6, message: translate("errors.pinTooShort") },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <View>
                <Text style={style.fieldTitle}>{translate("common.pin")}</Text>
                <CodeField
                  ref={ref}
                  {...props}
                  onFocus={() => {
                    clearErrors("pin")
                  }}
                  value={value}
                  onChangeText={(e) => {
                    clearErrors("pin")
                    onChange(e)
                  }}
                  cellCount={6}
                  rootStyle={style.mb10}
                  keyboardType="number-pad"
                  renderCell={({ index, symbol, isFocused }) => (
                    <Text
                      key={index}
                      style={[
                        style.cell,
                        { borderColor: isFocused && companyTheme.first },
                        error?.message && style.errorCell,
                      ]}
                      onLayout={getCellOnLayoutHandler(index)}
                    >
                      {symbol ? "•" : isFocused ? <Cursor /> : null}
                    </Text>
                  )}
                />
                {error?.message && (
                  <ErrorMessage customMessage={error?.message} errorType={error?.type} />
                )}
              </View>
            )
          }}
        />
        <View
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
            marginTop: 15,
          }}
        >
          <CheckBox checked={isChecked} onChange={setIsChecked} label="Remember Me" />

          <Text
            onPress={() => navigation.navigate("forgetPin")}
            style={[style.forgetPinText, { color: companyTheme.first }]}
          >
            {translate("loginScreen.forgetPin")}
          </Text>
        </View>

        <View style={style.buttonContainer}>
          {/* WH */}

          <Button
            loading={isLoading}
            onPress={handleSubmit(onSubmit)}
            style={style.loginButton}
            title={translate("common.login")}
          />

          <TouchableOpacity
            activeOpacity={0.7}
            onPress={() => navigation.navigate("register")}
            style={{
              backgroundColor: "white",
              borderColor: companyTheme.first,
              borderWidth: 1,
              borderRadius: 12,
              width: "100%",
              paddingVertical: 10,
              alignItems: "center",
            }}
          >
            <Text
              style={{
                color: companyTheme.first,
                fontFamily: typography.primary.normal,
                fontSize: 14,
              }}
            >
              {translate("loginScreen.signUp")}
            </Text>
          </TouchableOpacity>

          {/* <View style={{ alignItems: "center", marginTop: 10 }}>
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => navigation.navigate("existing")}
              style={{ paddingVertical: 5, paddingHorizontal: 10 }}
            >
              <Text
                style={{
                  color: companyTheme.first,
                  fontFamily: typography.primary.normal,
                  fontSize: 12,
                  borderBottomWidth: 1,
                  borderBottomColor: companyTheme.first,
                }}
              >
                {translate("loginScreen.existing")}
              </Text>
            </TouchableOpacity>
          </View> */}
        </View>
      </View>
    </Screen>
  )
})
